import { mdiClose } from '@mdi/js';
import dateFns from 'date-fns';
import _ from 'lodash';
import React, {useContext, useEffect, useState} from 'react';
import { ClaimResponsibilityType } from '../../../api';
import { AuthContext, MessagingContext } from '../../../services';
import { ResponsibilityBadge } from '../../badge';
import * as S from './compose-search.styles';

interface IClaim {
    id?: string;
    fromDos?: string;
    throughDos?: string;
    patientFirst?: string;
    patientLast?: string;
    primaryPayerName?: string;
    claimResponsibilityType?: ClaimResponsibilityType;
}

interface IComposeSearchProps {
    disabled?: boolean;
    onClaimSelected: (claimId: string) => void;
}

// FIXME: revisit to implement search handler
export const ComposeSearch: React.FunctionComponent<IComposeSearchProps> = (
    props: IComposeSearchProps,
): React.ReactElement => {
    const { disabled, onClaimSelected } = props;
    const context = useContext(MessagingContext);
    const [claims, setClaims] = useState<IClaim[]>([]);
    const [searchResults, setSearchResults] = useState<React.ReactElement[]>([]);
    const authContext = useContext(AuthContext);
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
        updateSearchResults();
    }, [claims]);

    const handleChange = async (term?: string): Promise<void> => {
        console.log("handleChangeCalled for " + term);
        if (_.isUndefined(term) || term.length === 0) {
            setOpen(false);
            setLoading(false);
            setSearchTerm('');
        } else {
            setLoading(true);
            setSearchTerm(term);
            try {
                if(context.onClaimSearch) {
                    const res = await (context.onClaimSearch(term));
                    if (res) {
                        setClaims(res);
                    }
                    setLoading(false);
                }
            } catch (err) {
                // NB: ignore cancel errors due to continued typing
            }
        }
    };
    const handleFocus = (): void => {
        setOpen(true);
    };
    const handleClaimAction = (claimId?: string): (() => void) => {
        return (): void => {
            if (claimId) {
                onClaimSelected(claimId);
            }
        };
    };

    function updateSearchResults() {
        const localSearchResults = _.map(
            claims,
            (claim: IClaim): React.ReactElement => {
                const formatDate = (d: Date): string => dateFns.format(d, 'MMM D');
                const parse = (d: string | undefined): Date => (d ? dateFns.parse(d) : new Date());

                const from = claim.fromDos ? formatDate(parse(claim.fromDos)) : undefined;
                const through = claim.throughDos ? formatDate(parse(claim.throughDos)) : undefined;
                const serviceDate = through ? `${from} – ${through}` : from;

                const totalCharges = _.get(claim, 'chargeAmount.amount', Number.NaN);
                return (
                    <S.SearchResult key={claim.id}>
                        <S.SearchResultRow onClick={handleClaimAction(claim.id)}>
                            <S.SearchResultCell>{serviceDate}</S.SearchResultCell>
                            <S.SearchResultCell>
                                {claim.patientFirst} {claim.patientLast}
                            </S.SearchResultCell>
                        </S.SearchResultRow>
                        <S.SearchResultRow onClick={handleClaimAction(claim.id)}>
                            <S.SearchResultCell>
                                <ResponsibilityBadge size="small" responsibility={claim.claimResponsibilityType} />
                            </S.SearchResultCell>
                            <S.SearchResultCell>{claim.primaryPayerName}</S.SearchResultCell>
                            <S.SearchResultCell>${!Number.isNaN(totalCharges) ? totalCharges : 0}</S.SearchResultCell>
                        </S.SearchResultRow>
                    </S.SearchResult>
                );
            },
        );
        setSearchResults(localSearchResults);
    }

    const doClose = (): void => {
        authContext.cancelGroup('search');
        setOpen(false);
    };

    const renderControls = (): React.ReactNode => {
        return (
            <S.Controls>
                <S.Spacer />
                <S.CloseButton onClick={doClose} variant="fab-light">
                    <S.CloseIcon path={mdiClose} size={1} />
                </S.CloseButton>
            </S.Controls>
        );
    };

    return (
        <S.Search>
            <S.Caption>What claim does it relate to? (Optional)</S.Caption>
            <S.SearchInput
                disabled={disabled}
                open={open}
                text={searchTerm}
                placeholder="Search for a claim"
                controls={renderControls()}
                onChange={handleChange}
                onFocus={handleFocus}
                loading={loading}
                results={searchResults}
                disableEscapeClose={true}
            />
        </S.Search>
    );
};
